import React, { useState } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Alert, Box, Container, Paper, Tab, Tabs } from '@mui/material';

import { useGetProperty } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import { isLTLEHotelOfferType } from '~/utils/offer';

import OfferBuilderSidebar from '../OfferBuilderSidebar';

import OfferBuilderImagesVideo from './ImagesVideo';
import OfferBuilderPropertyInformation, { formatPropertyLocation } from './OfferBuilderPropertyInformation';
import type { OfferBuilderProperty } from './OfferBuilderPropertyInformation';
import OfferPackages from './OfferPackages';
import OfferBuilderRooms from './Rooms';
import useOfferBuilderMedia from './useOfferBuilderMedia';

const DEFAULT_TAB = 'Property information';
const IMAGES_VIDEO_TAB = 'Images & Video';
const OFFER_PACKAGES_TAB = 'Offer packages';
const ROOMS_TAB = 'Rooms';

const CONTENT_TABS = [
  DEFAULT_TAB,
  ROOMS_TAB,
  OFFER_PACKAGES_TAB,
  'Default content',
  'Generated content',
  IMAGES_VIDEO_TAB,
];

export default function OfferBuilderContentPage() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);

  const { data: offer, isLoading: isLoadingOffer, error: offerError } = useGetOffer(offerId);
  const isSupportedOfferType = !!offer?.type && isLTLEHotelOfferType(offer.type);
  const hasOfferLoadError = !isLoadingOffer && !!offerError && !offer;
  const shouldRenderContentTabs = isLoadingOffer || (!!offer && isSupportedOfferType);
  const propertyId = isSupportedOfferType
    ? offer?.packages?.find((offerPackage) => offerPackage?.fk_property_id)?.fk_property_id
    : undefined;
  const vendorId = offer?.vendor_account_id;

  const {
    data: property,
    isLoading: isLoadingProperty,
    error: propertyError,
  } = useGetProperty<OfferBuilderProperty>({
    propertyId,
    enabled: isSupportedOfferType,
  });

  const hotelName = property?.name || propertyId || 'Property';
  const propertyLocation = formatPropertyLocation(property);
  const media = useOfferBuilderMedia({
    images: offer?.images,
    propertyImages: property?.images,
    propertyRoomTypes: property?.room_types,
    videos: offer?.videos,
  });

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Offer Builder | {hotelName}</title>
      </Helmet>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '280px minmax(0, 1fr)' } }}>
        <OfferBuilderSidebar hotelCode={property?.hotel_code} hotelName={hotelName} location={propertyLocation} />

        <Box sx={{ px: { xs: 2, md: 4 }, py: 3, pb: 14 }}>
          {hasOfferLoadError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Unable to load offer details.
            </Alert>
          )}

          {!isLoadingOffer && offer && !isSupportedOfferType && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Offer Builder is only available for flash offers.
            </Alert>
          )}

          {shouldRenderContentTabs && (
            <>
              <Paper sx={{ position: 'sticky', top: 0, zIndex: 2, mb: 3 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_event, value) => setActiveTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {CONTENT_TABS.map((tab) => (
                    <Tab key={tab} value={tab} label={tab} />
                  ))}
                </Tabs>
              </Paper>

              {activeTab === DEFAULT_TAB && (
                <OfferBuilderPropertyInformation
                  property={property}
                  vendorId={vendorId}
                  isLoadingProperty={isLoadingOffer || isLoadingProperty}
                  offerError={offerError}
                  propertyError={propertyError}
                />
              )}
              {activeTab === ROOMS_TAB && (
                <OfferBuilderRooms
                  media={media}
                  offerPackages={offer?.packages}
                  isLoadingProperty={isLoadingOffer || isLoadingProperty}
                  onOpenImagesVideo={() => setActiveTab(IMAGES_VIDEO_TAB)}
                  propertyError={propertyError}
                  propertyId={propertyId}
                  propertyName={hotelName}
                  propertyRoomTypes={property?.room_types}
                />
              )}
              {activeTab === OFFER_PACKAGES_TAB && (
                <OfferPackages
                  offerId={offerId}
                  packages={offer?.packages}
                  property={property}
                  propertyRoomTypes={property?.room_types}
                  offerType={offer?.type}
                  isLoadingOffer={isLoadingOffer}
                  offerError={offerError}
                />
              )}
              {activeTab === IMAGES_VIDEO_TAB && (
                <OfferBuilderImagesVideo
                  media={media}
                  propertyId={property?.id}
                  propertyLogoId={property?.logo_id_cloudinary_external}
                  propertyRoomTypes={property?.room_types}
                />
              )}
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
}
